import React, { useEffect } from 'react';
import { Paper, Grid, Box, Link } from '@mui/material';
import { useState } from 'react';
import { isEmpty } from 'lodash';
import { isValidEmail } from '../../../utilities/Validation';
import { createNewUser, findUser } from '../../../clients/bettercallkyle/BckApi';
import { registerProfile } from '../../../clients/profile/ProfileClient';
import { showNotificationBar } from '../../../actions/error-pages/NotificationBarActions';
import {StyledTextField} from "../../common/StyledTextField";
import { LoadingButton, TextButton } from "../../common/StyledButtons";
import {DesktopBox, MobileBox} from "../../common/Boxes";
import {LINK_TEXT_STYLE} from "../../../constants/Styles";
import background from "../../../assets/backgrounds/city-scape-background-left.webp";
import { ACCEPTABLE_USE_POLICY } from '../../../assets/legal/AcceptableUsePolicy';
import { PRIVACY_POLICY } from '../../../assets/legal/PrivacyPolicy';

const RegistrationForm = ({ history }) => {

  const [ firstName, setFirstName ] = useState();
  const [ lastName, setLastName ] = useState();
  const [ email, setEmail ] = useState();
  const [ password, setPassword ] = useState();
  const [ confirmPassword, setConfirmPassword ] = useState();

  const [imageLoaded, setImageLoaded] = useState(false);
  const [textVisible, setTextVisible] = useState(false);

  const [ errors, setErrors ] = useState([]);
  const [ isLoading, setIsLoading ] = useState(false);

  useEffect(() => {
    if (imageLoaded) {
      const timer = setTimeout(() => {
        setTextVisible(true);
      }, 250); // delay before text fades in
      return () => clearTimeout(timer);
    }
  }, [imageLoaded]);

  const handleLoginOnClick = (e) => {
    e.stopPropagation();
    history.replace('/login');
  }

  const handleShowTermsAndConditions = (event) => {
    var win = window.open('/terms', '_blank');
    win.focus();
  }

  const handleShowPrivacyPolicy = (event) => {
    var win = window.open('/privacy', '_blank');
    win.focus();
  }

  const isValidForm = () => {
    const foundErrors = [];
    if (!firstName || isEmpty(firstName)) foundErrors.push('firstName');
    if (!lastName || isEmpty(lastName)) foundErrors.push('lastName');
    if (!email || isEmpty(email) || !isValidEmail(email)) foundErrors.push('email');
    const minLength = 8;
    const hasUpper = /[A-Z]/.test(password);
    const hasLower = /[a-z]/.test(password);
    const hasDigit = /\d/.test(password);
    if (!password || password.length < minLength || !hasUpper || !hasLower || !hasDigit) foundErrors.push('password');
    if (!foundErrors.includes('password') && password !== confirmPassword) foundErrors.push('confirmPassword');
    setErrors(foundErrors);
    return foundErrors.length === 0;
  }

  const handleRegister = async () => {

    if (!isValidForm()) return;
    setIsLoading(true);

    try {
      const accepted_policies = [ACCEPTABLE_USE_POLICY, PRIVACY_POLICY].map(policy => ({
        version: policy.version,
        type: policy.type,
        accepted_timestamp: Date.now()
      }))

      await createNewUser(firstName, lastName, email, password, accepted_policies);

      history.push({
        pathname: '/verify',
        state: { user: { email, password }}
      });
    } catch (err) {
      const awaitedErr = await err;
      if(awaitedErr.code === 'UserAlreadyExists') {
        setErrors(['email-exists'])
        // showNotificationBar('error', 'This email has already been registered. Please try logging in.');
      } else {
        console.error(awaitedErr);
        showNotificationBar('error', 'Oh no! Something went wrong when creating the account. Please try again.');
      }
    } finally {
      setIsLoading(false)
    }
  }

  const renderRegistrationSection = () => (
    <Box sx={{ width: '100%', maxWidth: '500px' }}>
      <h5 style={{ textAlign: 'center', margin: 0, fontSize: '14px' }}>BCK Business Manager</h5>
      <h3 style={{ textAlign: 'center', margin: '8px 0 0', fontSize: '28px' }}>Create an Account</h3>
      <p style={{ textAlign: 'center', marginBottom: '16px' }}>
        Simply provide some information below and let's get started.
      </p>
  
      <Box component={Grid} container spacing={1}>
        <Grid item xs={12} sm={6}>
          <StyledTextField
            fullWidth
            error={errors.includes('firstName')}
            helperText={errors.includes('firstName') && 'First name is required'}
            value={firstName}
            disabled={isLoading}
            type="text"
            label="*First Name"
            onChange={(e) => setFirstName(e.target.value)}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <StyledTextField
            fullWidth
            error={errors.includes('lastName')}
            helperText={errors.includes('lastName') && 'Last name is required'}
            value={lastName}
            disabled={isLoading}
            type="text"
            label="*Last Name"
            onChange={(e) => setLastName(e.target.value)}
          />
        </Grid>
        <Grid item xs={12}>
          <StyledTextField
            fullWidth
            error={errors.includes('email') || errors.includes('email-exists')}
            helperText={
              errors.includes('email') && 'A valid email is required' ||
              (errors.includes('email-exists') && (
                <>
                  This email is already registered, please try <a href="/login">signing in</a>.
                </>
              ))
            }
            value={email}
            disabled={isLoading}
            type="email"
            label="*E-Mail"
            onChange={(e) => setEmail(e.target.value)}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <StyledTextField
            fullWidth
            error={errors.includes('password')}
            helperText={errors.includes('password') && 'Your password must be at least 8 characters, with one uppercase letter, one lowercase letter and one number.'}
            value={password}
            disabled={isLoading}
            type="password"
            label="*Password"
            onChange={(e) => setPassword(e.target.value)}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <StyledTextField
            fullWidth
            error={errors.includes('confirmPassword')}
            helperText={errors.includes('confirmPassword') && 'The passwords must match.'}
            value={confirmPassword}
            disabled={isLoading}
            type="password"
            label="*Confirm Password"
            onChange={(e) => setConfirmPassword(e.target.value)}
          />
        </Grid>
      </Box>
  
      <Box sx={{ mb: 2 }}>
        <p style={{ marginBottom: 8 }}>
          By clicking 'I Agree & Continue' below you are accepting the{' '}
          <span style={LINK_TEXT_STYLE} onClick={handleShowTermsAndConditions}>Terms of Use</span> and the{' '}
          <span style={LINK_TEXT_STYLE} onClick={handleShowPrivacyPolicy}>Privacy Policy</span> of Better Call Kyle, LLC.
        </p>
      </Box>
  
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
        <TextButton onClick={handleLoginOnClick} disabled={isLoading}>
          Sign In To Existing Account
        </TextButton>
        <LoadingButton onClick={handleRegister} disabled={isLoading} isLoading={isLoading}>
          I Agree & Continue
        </LoadingButton>
      </Box>
    </Box>
  );
  
  const renderImageSection = () => (
    <Grid item xs={12} md={5}>
      <Box
        sx={{
          height: '100%',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Background Image with fade-in */}
        <img
          src={background}
          alt="background"
          onLoad={() => setImageLoaded(true)}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            opacity: imageLoaded ? 1 : 0,
            transform: imageLoaded ? 'scale(1)' : 'scale(1.05)',
            transition: 'opacity 1s ease, transform 2.5s ease',
            zIndex: 1,
          }}
        />
  
        {/* Green Overlay */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: '#10AA00',
            opacity: 0.75,
            zIndex: 2,
            pointerEvents: 'none',
          }}
        />
  
        {/* Text Content with fade/slide-in */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            textAlign: 'left',
            zIndex: 3,
            color: 'white',
            px: 2,
            opacity: textVisible ? 1 : 0,
            transform: textVisible ? 'translateY(0)' : 'translateY(20px)',
            transition: 'opacity 0.6s ease, transform 0.6s ease',
          }}
        >
          <Box sx={{ minWidth: '200px', maxWidth: '60%' }}>
            <h3 style={{ color: '#fff', fontSize: '40px', marginBottom: 0 }}>
              The next step in your business journey starts here.
            </h3>
            <p style={{ fontSize: '18px' }}>
              Join BCK and bring your vision online.
            </p>
          </Box>
        </Box>
      </Box>
    </Grid>
  );
  
  
  const renderDesktopView = () => (
    <DesktopBox sx={{ width: '100%', height: 'calc(100vh - 64px)' }}>
      <Grid container sx={{ height: '100%' }}>
        {/* Left - Image */}
        {renderImageSection()}
    
        {/* Right - Form */}
        <Grid item xs={12} md={7}>
          <Paper
            elevation={3}
            sx={{
              height: '100%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              backgroundColor: '#fff',
              boxSizing: 'border-box',
              overflow: 'hidden',
            }}
          >
            {renderRegistrationSection()}
          </Paper>
        </Grid>
      </Grid>
    </DesktopBox>
  

  )

  const renderMobileView = () => (
    <MobileBox
      sx={{
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: 'calc(100vh - 64px)',
        backgroundColor: '#fff',
        paddingTop: '20px',
        paddingBottom: '40px',
        paddingLeft: '20px',
        paddingRight: '20px'
      }}
    >
      {renderRegistrationSection()}
    </MobileBox>
  )
  

  return (
    <div>
      {renderDesktopView()}
      {renderMobileView()}
    </div>
  )
}

export default RegistrationForm;
